import { useContext, useEffect, useState } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { PublicAdvisor } from "protogen/common_pb";
import { SYSTEM_ADVISOR_REF } from "components/common/types";
import useIsMobile from "components/hooks/useIsMobile";
import { CircleUserRound, Phone, Mail, X } from "lucide-react";
import { UserAvatar } from "components/common/CurrentUserAvatar";
import LinkRouter from "components/navigation/LinkRouter";
import { formatUSPhoneNumber } from "common/utils";
import { useListReviews } from "services/review";
import { CurrentUserContext } from "components/context/RequireAuth";
import AddReviewDialog from "components/review/AddReviewDialog";

const TWO_DAYS_SEC = 2 * 24 * 60 * 60;

const AdvisorInfo = ({
  advisor,
  isDeactivated,
  openReview = false,
  familyCreatedAtSec,
  productPrompts,
  onDismissReview,
}: {
  advisor: PublicAdvisor;
  isDeactivated?: boolean;
  openReview?: boolean;
  familyCreatedAtSec?: bigint;
  productPrompts?: any;
  onDismissReview?: () => void;
}) => {
  const isSystemAdvisor = advisor?.ref === SYSTEM_ADVISOR_REF;
  const isMobile = useIsMobile();
  const currentUser = useContext(CurrentUserContext);
  const [hasReview, setHasReview] = useState<boolean>(false);
  const [checkComplete, setCheckComplete] = useState<boolean>(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const { request: listReviews } = useListReviews((response) => {
    if (response?.reviews) {
      const userReview = response.reviews.find(
        (r) => r.submitterRef === currentUser?.ref,
      );
      setHasReview(!!userReview);
      if (!userReview && openReview) {
        setReviewDialogOpen(true);
      }
    }
    setCheckComplete(true);
  });

  useEffect(() => {
    if (advisor?.ref && !isSystemAdvisor && !isDeactivated) {
      listReviews({ subjectRef: advisor.ref });
    } else {
      setCheckComplete(true);
    }
  }, [advisor?.ref, isSystemAdvisor, isDeactivated]);

  const familyIsOldEnough =
    !!familyCreatedAtSec &&
    Date.now() / 1000 - Number(familyCreatedAtSec) >= TWO_DAYS_SEC;
  const showReviewButton =
    checkComplete &&
    !hasReview &&
    !isSystemAdvisor &&
    !isDeactivated &&
    !!advisor?.ref &&
    familyIsOldEnough &&
    productPrompts?.reviewPromptDismissed !== "dismissed";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant={isMobile ? "h3" : "h4"}>Family Advisor</Typography>
        {showReviewButton && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Button variant="text" onClick={() => setReviewDialogOpen(true)}>
                Share a review
              </Button>
              <IconButton
                size="small"
                onClick={onDismissReview}
                sx={{ color: "text.tertiary" }}
              >
                <X size={16} />
              </IconButton>
            </Box>
          </>
        )}
        <AddReviewDialog
          open={reviewDialogOpen}
          onClose={(created) => {
            setReviewDialogOpen(false);
            if (created) {
              setHasReview(true);
            }
          }}
          advisorRef={advisor?.ref}
        />
      </Box>
      {isDeactivated && <Box />}
      {isSystemAdvisor && !isDeactivated && (
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <CircleUserRound size={48} color="#8E9598" />
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography variant="bodyHeavy">
              Advisor match in progress...
            </Typography>
            <Typography variant="body">
              We're working on finding the perfect Advisor for your family.
            </Typography>
          </Box>
        </Box>
      )}
      {advisor && !isSystemAdvisor && !isDeactivated && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <UserAvatar size={80} user={advisor} memberLink={true} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <LinkRouter to={`/profile/${encodeURIComponent(advisor.ref)}`}>
              <Typography
                variant="body"
                color="text.primary"
                sx={{
                  cursor: "pointer",
                  lineHeight: "18px",
                  fontWeight: "600",
                }}
              >
                {advisor.displayName}
              </Typography>
            </LinkRouter>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "12px",
                alignItems: "center",
              }}
            >
              <Phone height="20px" width="20px" color="#198282" />
              <Button
                sx={(theme) => ({
                  height: "unset",
                  fontWeight: 500,
                  lineHeight: "unset",
                  color: theme.palette.text.primary,
                })}
                variant="text"
                component="a"
                href={`sms:${advisor?.advisorPhone!}`}
              >
                {formatUSPhoneNumber(advisor?.advisorPhone)}
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "12px",
                alignItems: "center",
              }}
            >
              <Mail height="20px" width="20px" color="#198282" />
              <Button
                sx={(theme) => ({
                  height: "unset",
                  fontWeight: 500,
                  lineHeight: "unset",
                  color: theme.palette.text.primary,
                })}
                variant="text"
                component="a"
                href={`mailto:${advisor?.username!}`}
              >
                {advisor?.username}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AdvisorInfo;
